"use client";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import dynamic from "next/dynamic";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { useRouter, useSearchParams } from "next/navigation";
import { KeyNames } from "@packages/lib/utlils/filters/filterJson";
import { useFilterContext } from "@packages/lib/utlils/use-context/filter-context";
import {
  UniveristyList,
  SearchFilterButtonsIF,
  ButtonsREquiredIF,
} from "@packages/lib/interfaces/scholarship";
import Image from "next/image";
import {
  deleteDocumentCookie,
  setDocumentCookie,
} from "@packages/lib/utlils/cookie-utils";
interface AppliedFilters {
  year?: string[];
  month?: string[];
  location?: string[];
  university?: string[];
  qualification?: string[];
  studyMethod?: string[];
  locationType?: string[];
  russellGroup?: string[];
  studyMode?: string[];
  subject?: string[];
  distance?: string[];
  award_type?: string;
  award_coverage?: string;
  nationality?: string;
}

interface ExtendedUniversityList extends UniveristyList {
  scholarshipCount?: number;
}

const UcasComponent = dynamic(
  () =>
    import(
      "@packages/shared-components/common-utilities/popups/ucas-calculator/ucascomponent"
    ),
  { ssr: false }
);
const createCookieHandler = () => {
  let cache: Record<string, any> | null = null;
  let lastCookieString = "";

  return {
    get: (): Record<string, any> => {
      if (typeof document === "undefined") return {};
      const current = document.cookie;
      if (current === lastCookieString && cache) return cache;

      lastCookieString = current;
      const value = current.split("filter_param=")[1]?.split(";")[0];
      try {
        cache = JSON.parse(decodeURIComponent(value || "{}"));
      } catch {
        cache = {};
      }
      return cache || {};
    },
    clear: () => {
      cache = null;
      lastCookieString = "";
    },
  };
};

const cookieHandler = createCookieHandler();

const DEFAULT_BUTTONS: ButtonsREquiredIF = {
  university: false,
  addYourGrades: true,
  filter: true,
  reset: true,
  additional: {
    location: true,
    studyLevel: true,
    university: true,
    subject: true,
    year: true,
  },
};

const SearchFilterButtons = ({
  frompage,
  isScholarshipPage,
  buttonRequired = DEFAULT_BUTTONS,
  universityList = [],
  selectedParams,
}: SearchFilterButtonsIF) => {
  const { setrouterEnd } = useFilterContext();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [filterCount, setFilterCount] = useState(0);
  const [gradeCount, setGradeCount] = useState("");
  const [isUcasPopupOpen, setUcasPopupOpen] = useState(false);
  const [scrolled, setScroll] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [universitySearch, setUniversitySearch] = useState<UniveristyList>({
    collegeId: 0,
    collegeName: "",
    collegeNameAlias: "",
    collegeNameDisplay: "",
    collegeTextKey: "",
  });
  const [filteredList, setFilteredList] = useState<ExtendedUniversityList[]>(
    []
  );
  const [urlQueries, SetURLQueries] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const keyName = useMemo(() => KeyNames(), []);
  const filterCookie = useMemo(() => cookieHandler.get() || {}, [searchParams]);
  const isWhatuni = useMemo(() => process.env.PROJECT === "Whatuni", []);

  const {
    locationFilterCount,
    subjectFilterCount,
    studeyLevel,
    subjectWithOutSplit,
    appliedFilters,
  } = useMemo((): {
    locationFilterCount: number;
    subjectFilterCount: number;
    studeyLevel: string;
    subjectWithOutSplit: string;
    appliedFilters: AppliedFilters;
  } => {
    const locationCount =
      (searchParams?.get(keyName?.location)?.split(" ")?.length || 0) +
      (filterCookie?.location?.split("+")?.length || 0);
    const subjectCount =
      (searchParams?.get(keyName?.subject)?.split(" ")?.length || 0) +
      (filterCookie?.subject?.split("+")?.length || 0);
    const level = selectedParams?.studyLevel || "";
    const subjectStr = Array.isArray(selectedParams?.subject)
      ? selectedParams.subject.join("+")
      : selectedParams?.subject || "";

    const filters: AppliedFilters = {
      year: searchParams?.get(keyName?.year)?.split(",") || undefined,
      month: searchParams?.get(keyName?.month)?.split(",") || undefined,
      location: searchParams?.get(keyName?.location)?.split(",") || undefined,
      university:
        searchParams?.get(keyName?.university)?.split(",") || undefined,
      qualification:
        searchParams?.get("qualification")?.split(",") || undefined,
      studyMethod:
        searchParams?.get(keyName?.studyMethod)?.split(",") || undefined,
      locationType:
        searchParams?.get(keyName?.locationType)?.split(",") || undefined,
      russellGroup:
        searchParams?.get(keyName?.russellGroup)?.split(",") || undefined,
      studyMode: searchParams?.get(keyName?.studyMode)?.split(",") || undefined,
      subject: searchParams?.get(keyName?.subject)?.split(",") || undefined,
      distance: searchParams?.get(keyName?.distance)?.split(",") || undefined,
      award_type: searchParams?.get(keyName.award_type) || undefined,
      award_coverage: searchParams?.get(keyName.award_coverage) || undefined,
      nationality: searchParams?.get(keyName.nationality) || undefined,
    };

    return {
      locationFilterCount: locationCount,
      subjectFilterCount: subjectCount,
      studeyLevel: level,
      subjectWithOutSplit: subjectStr,
      appliedFilters: filters,
    };
  }, [searchParams, filterCookie, selectedParams, keyName]);

  const getSelectedFiltersCountFromParams = useCallback(() => {
    if (!selectedParams) return 0;
    
    const hasValue = (val: any) => val && val !== "" && (!Array.isArray(val) || val.length > 0);
    
    return [
      selectedParams.studyLevel,
      selectedParams.subject,
      selectedParams.provider,
      selectedParams.nationality || appliedFilters.nationality,
      selectedParams.awardType || appliedFilters.award_type,
      selectedParams.awardCoverage || appliedFilters.award_coverage
    ].filter(hasValue).length;
  }, [selectedParams, appliedFilters]);

  const getSelectedFiltersCount = useCallback(() => {
    const totalCount = Object.values(appliedFilters).reduce(
      (acc: number, value) =>
        acc + (Array.isArray(value) ? value.filter(Boolean).length : 0),
      0
    );
    const cookieCopy = { ...filterCookie };
    delete cookieCopy?.subject;
    if (typeof document !== "undefined" && !document.referrer)
      delete cookieCopy?.score;
    return totalCount + (isWhatuni ? 2 : 1) + Object.keys(cookieCopy).length;
  }, [appliedFilters, filterCookie, isWhatuni]);

  const filterEvents = useCallback((eventName: string | null | undefined) => {
    emitter.emit("isfilterOpen", eventName);
    emitter.emit("pauseVideo");
  }, []);

  const toggleUcasPopup = useCallback((isOpen: boolean) => {
    setUcasPopupOpen(isOpen);
    document?.body.classList.toggle("overflow-y-hidden", isOpen);
  }, []);

  const resetFilter = useCallback(() => {
    emitter.emit("refreshFilters");
    setrouterEnd(true);

    const pathName = typeof window !== "undefined" ? window.location.pathname : "";
    const pathSegments = pathName?.split?.("/").filter(Boolean);
    const hasSearchOrCsearch = pathName?.includes("search") || pathName?.includes("csearch");
    
    // Build reset URL
    let resetPath;
    if (hasSearchOrCsearch) {
      const prefixPath = pathSegments[0];
      
      if (prefixPath === "money") {
        resetPath = "/money/scholarships/search/undergraduate";
      } else {
        resetPath = "/funding/scholarships";
      }
    } else {
      const subjectList = searchParams?.get(keyName?.subject)?.split(" ")?.[0] || "";
      const urlParams = new URLSearchParams();
      if (subjectList) urlParams.set(keyName?.subject, subjectList);
      resetPath = urlParams.toString() ? `?${urlParams.toString()}` : "?";
    }

    const cookieOps = [
      { name: "filter_param", value: "{}", action: "set" as const },
      { name: "min", action: "delete" as const },
      { name: "ucaspoint", action: "delete" as const },
      { name: "UCAS", action: "delete" as const },
    ];

    requestAnimationFrame(() => {
      cookieOps.forEach(({ name, value, action }) => {
        if (action === "delete") {
          deleteDocumentCookie({ name });
        } else {
          setDocumentCookie({ name, value: value || "" });
        }
      });

      try {
        sessionStorage.setItem("filter_param", "{}");
      } catch (e) {
        console.warn("SessionStorage not available:", e);
      }

      SetURLQueries("");
      cookieHandler.clear();
    });

    const prefetchAndNavigate = async () => {
      try {
        await router.prefetch(resetPath);
        router.push(resetPath);
      } catch {
        router.push(resetPath);
      }
    };
    prefetchAndNavigate();
  }, [searchParams, keyName, setrouterEnd, router]);

  const handleSearchClick = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!universitySearch?.collegeNameDisplay?.trim()) {
        setShowError(true);
        setIsDropdownOpen(true);
        return;
      }
      setShowError(false);
    },
    [universitySearch?.collegeNameDisplay]
  );

  const onSearchBoxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowError(false);
      const uniValue = e.target.value.toLowerCase();
      const previousCollegeTextKey = universitySearch?.collegeTextKey;

      setUniversitySearch((prev) => ({
        ...prev,
        collegeNameDisplay: uniValue,
        collegeTextKey: uniValue.trim() === "" ? "" : prev.collegeTextKey,
      }));

      setIsDropdownOpen(true);

      if (previousCollegeTextKey && uniValue.trim() === "") {
        const baseUrl =
          process.env.PROJECT === "PGS"
            ? subjectWithOutSplit
              ? `/funding/scholarships/search/${subjectWithOutSplit}`
              : "/funding/scholarships/search"
            : subjectWithOutSplit
              ? `/money/scholarships/search/${studeyLevel}/${subjectWithOutSplit}`
              : `/money/scholarships/search/${studeyLevel}`;

        router.push(urlQueries ? `${baseUrl}?${urlQueries}` : baseUrl);
      }

      if (uniValue.trim().length === 0) {
        setFilteredList(universityList as ExtendedUniversityList[]);
        return;
      }

      const searchTerms = uniValue.trim().split(/\s+/).filter(Boolean);
      const filteredUniList =
        universityList?.filter((item: UniveristyList) => {
          const name = item.collegeNameDisplay?.toLowerCase() || "";
          return searchTerms.some((term: string) => name.includes(term));
        }) || [];

      setFilteredList(filteredUniList as ExtendedUniversityList[]);
    },
    [
      universitySearch?.collegeTextKey,
      subjectWithOutSplit,
      studeyLevel,
      urlQueries,
      universityList,
      router,
    ]
  );

  const onSearchBoxClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      setIsDropdownOpen(true);
      const target = e.target as HTMLInputElement;
      const uniValue = (target.value || "").toLowerCase();

      if (uniValue.trim().length === 0) {
        setFilteredList(universityList as ExtendedUniversityList[]);
        return;
      }

      const searchTerms = uniValue.trim().split(/\s+/).filter(Boolean);
      const fullMatches =
        universityList?.filter((item: UniveristyList) =>
          (item.collegeNameDisplay || "").toLowerCase().includes(uniValue.trim())
        ) || [];

      if (fullMatches.length > 0) {
        setFilteredList(fullMatches as ExtendedUniversityList[]);
        return;
      }

      const termMatches =
        universityList?.filter((item: UniveristyList) => {
          const name = (item.collegeNameDisplay || "").toLowerCase();
          return searchTerms.some((term: string) => name.includes(term));
        }) || [];

      setFilteredList(termMatches as ExtendedUniversityList[]);
    },
    [universityList]
  );

  const handleCollegeClick = useCallback(
    (items: UniveristyList) => {
      setUniversitySearch((prev) => ({
        ...prev,
        collegeName: items.collegeName,
      }));
      setIsDropdownOpen(false);

      const isPGS = process.env.PROJECT === "PGS";
      const baseSegments = isPGS
        ? ["funding", "scholarships", "csearch"]
        : ["money", "scholarships", "csearch"];
      const pathSegments = [...baseSegments];

      if (!isPGS) pathSegments.push(studeyLevel);
      //if (subjectWithOutSplit) pathSegments.push(subjectWithOutSplit);
      pathSegments.push(items.collegeTextKey);

      const path = `/${pathSegments.join("/")}`;
      //router.push(urlQueries ? `${path}?${urlQueries}` : path);
      emitter.emit("refreshScholarshipFilters", {
        studyLevel: studeyLevel,
        university: items.collegeTextKey,
      });
      router.push(path);
    },
    [studeyLevel, subjectWithOutSplit, searchParams, router]
  );

  useEffect(() => {
    const newFilterCount = isScholarshipPage
      ? getSelectedFiltersCountFromParams()
      : getSelectedFiltersCount();
    const newGradeCount =
      searchParams?.has("score") ||
      (typeof document !== "undefined" &&
        document.referrer &&
        filterCookie?.score)
        ? "1"
        : "";

    setFilterCount((prev) => (prev !== newFilterCount ? newFilterCount : prev));
    setGradeCount((prev) => (prev !== newGradeCount ? newGradeCount : prev));
  }, [
    searchParams,
    filterCookie,
    isScholarshipPage,
    getSelectedFiltersCountFromParams,
    getSelectedFiltersCount,
  ]);

  useEffect(() => {
    if (!isScholarshipPage) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          let shouldScroll;
          if(selectedParams?.provider){
          shouldScroll = window.scrollY > 700;
          } else {
          shouldScroll = window.scrollY > 330;
          }
          setScroll((prev) => {
            if (prev !== shouldScroll) {
              document.body.classList.toggle("mb-[61px]", shouldScroll);
              return shouldScroll;
            }
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScholarshipPage]);

  useEffect(() => {
    if (!selectedParams?.provider || !universityList?.length) return;

    const universityValue = universityList.find((item: UniveristyList) =>
      item.collegeTextKey?.includes(selectedParams.provider)
    );

    if (universityValue) {
      setUniversitySearch((prev) =>
        prev.collegeTextKey !== universityValue.collegeTextKey
          ? universityValue
          : prev
      );
    }
  }, [universityList, selectedParams?.provider]);

  useEffect(() => {
    const { nationality, award_type, award_coverage } = appliedFilters;
    const queryParams = new URLSearchParams();
    if (nationality) queryParams.set("nationality", nationality);
    if (award_type) queryParams.set("award-type", award_type);
    if (award_coverage) queryParams.set("award-coverage", award_coverage);

    const newQuery = queryParams.toString();
    SetURLQueries((prev) => (prev !== newQuery ? newQuery : prev));
  }, [
    appliedFilters.nationality,
    appliedFilters.award_type,
    appliedFilters.award_coverage,
  ]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, {
      passive: true,
    });
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <>
    {isScholarshipPage && 
      <section className={`${isScholarshipPage && scrolled ? 'lg:py-[34px]' : 'hidden'}`}></section>
    }
  
     <section
      className={`${isScholarshipPage ? "py-[12px] items-center fixed bottom-[0] w-full" : "py-[16px] sticky top-0"} ${isScholarshipPage && scrolled ? " !block fixed w-full bottom-[0]" : " lg:relative lg:[unset]"} bg-grey-600 px-[12px] z-[4]`}
    >
      <div className="max-w-container mx-auto flex gap-[8px] small">
        {buttonRequired?.university && !scrolled && (
          <div className="hidden lg:flex h-[44px] w-[200px] px-[4px] items-center bg-white rounded-[32px] border border-neutral300 hover:border-primary-500 shadow-custom-1 gap-x-[10px]">
            <div className="flex relative">
              <input
                ref={inputRef}
                value={universitySearch?.collegeNameDisplay || ""}
                onChange={onSearchBoxChange}
                onClick={onSearchBoxClick}
                type="text"
                className="form-control w-full focus:outline-none pl-[24px] bg-transparent small text-black placeholder:text-gray-500 border-none"
                aria-label="submenu"
                placeholder="University name"
              />
              {isDropdownOpen && (
                <div
                  ref={dropdownRef}
                  className={`${isScholarshipPage ? "w-[343px] z-[5]" : "w-full z-[1]"} flex flex-col absolute bg-white shadow-custom-3 rounded-[8px] left-[0] top-[40px] w-[343px] overflow-hidden`}
                >
                  <div className="px-[16px] py-[12px] cursor-pointer">
                    {/* <p className="text-small font-semibold text-black tracking-[1px] leading-[18px] uppercase">
                      KEYWORD SEARCH FOR
                    </p> */}
                    {showError && (
                      <p className="text-red-500 mt-2 text-sm">
                        Please select a university
                      </p>
                    )}
                    {/* <p className="small text-primary-400">'University'</p> */}
                  </div>
                  <div className="flex px-[16px] py-[8px]">
                    <ul className="flex flex-wrap gap-[8px]">
                      <li className="flex text-nowrap select-none rounded-[4px] font-bold uppercase px-[8px] bg-grey-100 text-grey-500 xs-small">
                        University
                      </li>
                    </ul>
                  </div>
                  <ul className="custom-scrollbar-2 max-h-[205px] overflow-y-scroll mr-[4px]">
                    {filteredList.length > 0 ? (
                      filteredList.map((items: ExtendedUniversityList) => (
                        <li
                          key={items.collegeTextKey}
                          className="flex gap-[4px] hover:bg-blue-50 px-[16px] py-[10px] small cursor-pointer"
                          onClick={() => handleCollegeClick(items)}
                        >
                          <div className="hover:underline text-black">
                            {items.collegeNameDisplay}
                            <span className="text-grey500 ml-2">
                              {items.scholarshipCount || 0} Scholarships
                            </span>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="px-[16px] py-[10px] text-sm text-grey-500">
                        No universities found
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="submit"
              onClick={handleSearchClick}
              className="btn btn-primary h-[36px] min-w-[36px] p-[8px]"
            >
              <Image
                src="/static/assets/icons/search_icon.svg"
                width="18"
                height="18"
                alt="Search icon"
              />
            </button>
          </div>
        )}

        {buttonRequired?.addYourGrades && isWhatuni && (
          <button
            type="button"
            className="flex items-center justify-center gap-[8px] btn btn-primary grow w-fit px-[12px] lg:grow-0 lg:shrink-0"
            onClick={() => toggleUcasPopup(true)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10 1.875C10.641 1.875 11.1607 2.39467 11.1607 3.03571V8.83929H16.9643C17.6053 8.83929 18.125 9.35895 18.125 10C18.125 10.641 17.6053 11.1607 16.9643 11.1607H11.1607V16.9643C11.1607 17.6053 10.641 18.125 10 18.125C9.35895 18.125 8.83929 17.6053 8.83929 16.9643V11.1607H3.03571C2.39467 11.1607 1.875 10.641 1.875 10C1.875 9.35895 2.39467 8.83928 3.03571 8.83928L8.83929 8.83929V3.03571C8.83929 2.39467 9.35895 1.875 10 1.875Z"
                fill="#F9FAFB"
              />
            </svg>
            Add my grades {gradeCount && `(${gradeCount})`}
          </button>
        )}
        {buttonRequired?.addYourGrades && isUcasPopupOpen && (
          <UcasComponent
            onClose={() => toggleUcasPopup(false)}
            isUcasOpen={isUcasPopupOpen}
          />
        )}

        {buttonRequired?.filter && (
          <button
            type="button"
            onClick={() => filterEvents("all")}
            className="flex items-center justify-center gap-[8px] btn grow w-fit px-[12px] bg-primary-100 hover:bg-primary-200 text-grey300 lg:grow-0 lg:shrink-0"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3.90625V1.875M10 3.90625C8.87817 3.90625 7.96875 4.81567 7.96875 5.9375C7.96875 7.05933 8.87817 7.96875 10 7.96875M10 3.90625C11.1218 3.90625 12.0312 4.81567 12.0312 5.9375C12.0312 7.05933 11.1218 7.96875 10 7.96875M3.90625 16.0938C5.02808 16.0938 5.9375 15.1843 5.9375 14.0625C5.9375 12.9407 5.02808 12.0312 3.90625 12.0312M3.90625 16.0938C2.78442 16.0938 1.875 15.1843 1.875 14.0625C1.875 12.9407 2.78442 12.0312 3.90625 12.0312M3.90625 16.0938V18.125M3.90625 12.0312V1.875M10 7.96875V18.125M16.0938 16.0938C17.2156 16.0938 18.125 15.1843 18.125 14.0625C18.125 12.9407 17.2156 12.0312 16.0938 12.0312M16.0938 16.0938C14.9719 16.0938 14.0625 15.1843 14.0625 14.0625C14.0625 12.9407 14.9719 12.0312 16.0938 12.0312M16.0938 16.0938V18.125M16.0938 12.0312V1.875"
                stroke="#333333"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Filter ({filterCount})
          </button>
        )}

        {buttonRequired?.additional && (
          <div className="hidden lg:flex items-center justify-center gap-[8px] lg:shrink-0">
            {buttonRequired?.additional?.studyLevel && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("subject")}
              >
                {isWhatuni ? "Study level" : "Qualification"}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8L10 13L5 8"
                    stroke="#333F48"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {buttonRequired?.additional?.subject && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("subject")}
              >
                Subject {subjectFilterCount > 0 && `(${subjectFilterCount})`}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8L10 13L5 8"
                    stroke="#333F48"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {buttonRequired?.additional?.year && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("year")}
              >
                Year
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8L10 13L5 8"
                    stroke="#333F48"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {buttonRequired?.additional?.university && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("university")}
              >
                University
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8L10 13L5 8"
                    stroke="#333F48"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {buttonRequired?.additional?.location && frompage && (
              <button
                type="button"
                className="flex items-center gap-[8px] btn w-fit bg-grey-100 hover:bg-grey-200 text-grey300"
                onClick={() => filterEvents("location")}
              >
                Location {locationFilterCount > 0 && `(${locationFilterCount})`}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8L10 13L5 8"
                    stroke="#333F48"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {buttonRequired?.reset && (
          <button
            type="button"
            className="flex items-center justify-center gap-[4px] cursor-pointer px-0 text-grey-50 hover:underline xl:px-[16px] lg:shrink-0"
            onClick={resetFilter}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.8173 1.8173C2.24037 1.39423 2.9263 1.39423 3.34937 1.8173L8 6.46794L12.6506 1.8173C13.0737 1.39423 13.7596 1.39423 14.1827 1.8173C14.6058 2.24037 14.6058 2.9263 14.1827 3.34937L9.53206 8L14.1827 12.6506C14.6058 13.0737 14.6058 13.7596 14.1827 14.1827C13.7596 14.6058 13.0737 14.6058 12.6506 14.1827L8 9.53206L3.34937 14.1827C2.9263 14.6058 2.24037 14.6058 1.8173 14.1827C1.39423 13.7596 1.39423 13.0737 1.8173 12.6506L6.46794 8L1.8173 3.34937C1.39423 2.9263 1.39423 2.24037 1.8173 1.8173Z"
                fill="white"
              />
            </svg>
            Reset
          </button>
        )}
      </div>
    </section>
    </>
   
  );
};

export default SearchFilterButtons;
